import { useQuery } from '@tanstack/react-query';
import { api, qs, unwrap } from '../api/client';
import { useAccounts, useAutomations } from '../api/hooks';
import type { Items, Media } from '../api/types';
import { DataTable, Empty, ErrorAlert, fmtDate, PageHeader } from '../components/ui';

export function StoriesPage() {
  const accounts = useAccounts();
  const automations = useAutomations();
  const stories = useQuery({
    queryKey: ['stories', accounts.data?.items.map((a) => a.id)],
    queryFn: async () =>
      (
        await Promise.all(
          (accounts.data?.items ?? []).map((a) =>
            unwrap(
              api.get<Items<Media>>(
                `/portal/social-accounts/${a.id}/media${qs({ kind: 'story' })}`,
              ),
            ).then((r) => r.items.map((m) => ({ ...m, username: a.username }))),
          ),
        )
      ).flat(),
    enabled: (accounts.data?.items.length ?? 0) > 0,
  });
  const storyAutomations = (automations.data?.items ?? []).filter(
    (a) => a.triggerType === 'story_reply',
  );
  return (
    <>
      <PageHeader
        title="Stories"
        subtitle="Recently synced stories on your accounts and the story-reply automations that run on them"
      />
      <ErrorAlert error={stories.error} />
      <div className="grid grid-2">
        <DataTable>
          <table className="table">
            <thead>
              <tr>
                <th>Story</th>
                <th>Account</th>
                <th>Published</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {(stories.data ?? []).map((s) => (
                <tr key={s.id}>
                  <td className="mono">{s.externalId}</td>
                  <td>@{s.username}</td>
                  <td>{fmtDate(s.publishedAt)}</td>
                  <td>{s.mediaType ?? '—'}</td>
                </tr>
              ))}
              {stories.data?.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <Empty>No stories synced yet. Use Refresh on Social Accounts.</Empty>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </DataTable>
        <DataTable>
          <table className="table">
            <thead>
              <tr>
                <th>Story reply automation</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {storyAutomations.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td>{a.status}</td>
                </tr>
              ))}
              {storyAutomations.length === 0 ? (
                <tr>
                  <td colSpan={2}>
                    <Empty>
                      No story automations. Create one from Automations with scope "Story".
                    </Empty>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </DataTable>
      </div>
    </>
  );
}
